/**
 * Monthly Consultation PDF document root (print-only).
 * Composes PDF chapters in print order (Cover → Overview → Focus → … → Letter)
 * and injects scoped print CSS under data-mc-pdf-root.
 */

import React from "react";
import type { MonthlyConsultationBundle } from "../../../utils/forecast/monthlyConsultation";
import { MONTHLY_CONSULTATION_PDF_STYLES } from "./monthlyConsultationPdfStyles";
import {
  PdfChapterActivations,
  PdfChapterArchetype,
  PdfChapterCareer,
  PdfChapterContinuity,
  PdfChapterConvergence,
  PdfChapterCover,
  PdfChapterFailure,
  PdfChapterFocus,
  PdfChapterHealth,
  PdfChapterLetter,
  PdfChapterNobleman,
  PdfChapterRarity,
  PdfChapterRelationships,
  PdfChapterScorecard,
  PdfChapterStars,
  PdfChapterWealth,
  PdfChapterWeeks,
} from "./chapters";

/**
 * Props for the print-only PDF document.
 */
export interface MonthlyConsultationPdfDocumentProps {
  /** Full monthly consultation payload rendered by each PDF chapter. */
  bundle: MonthlyConsultationBundle;
}

/**
 * Print PDF document: injects MC print CSS and renders chapters in print order
 * (cover → overview → focus → rarity → activations → stars → alignment → …).
 * Page numbers count rendered sheets. Visible sheets = 17.
 */
export const MonthlyConsultationPdfDocument: React.FC<
  MonthlyConsultationPdfDocumentProps
> = ({ bundle }) => {
  return (
    <div data-mc-pdf-root="">
      <style>{MONTHLY_CONSULTATION_PDF_STYLES}</style>
      {/*
        Sequential page numbers count RENDERED sheets (17 visible).
        Weeks is restored as its own sheet after Failure. Extras sit in Canva
        slots: Activations 5, Stars 6, Convergence/Alignment 7, Scorecard 8.
        Close: Failure 13, Weeks 14, Nobleman 15, Continuity 16, Letter 17.
      */}
      <PdfChapterCover bundle={bundle} pageNumber={1} />
      <PdfChapterArchetype bundle={bundle} pageNumber={2} />
      <PdfChapterFocus bundle={bundle} pageNumber={3} />
      <PdfChapterRarity bundle={bundle} pageNumber={4} />
      <PdfChapterActivations bundle={bundle} pageNumber={5} />
      <PdfChapterStars bundle={bundle} pageNumber={6} />
      <PdfChapterConvergence bundle={bundle} pageNumber={7} />
      <PdfChapterScorecard bundle={bundle} pageNumber={8} />
      <PdfChapterCareer bundle={bundle} pageNumber={9} />
      <PdfChapterWealth bundle={bundle} pageNumber={10} />
      <PdfChapterRelationships bundle={bundle} pageNumber={11} />
      <PdfChapterHealth bundle={bundle} pageNumber={12} />
      <PdfChapterFailure bundle={bundle} pageNumber={13} />
      <PdfChapterWeeks bundle={bundle} pageNumber={14} />
      <PdfChapterNobleman bundle={bundle} pageNumber={15} />
      <PdfChapterContinuity bundle={bundle} pageNumber={16} />
      <PdfChapterLetter bundle={bundle} pageNumber={17} />
    </div>
  );
};

export default MonthlyConsultationPdfDocument;
